/**
 * The eight geography slots (docs/design-ideology.json `geography_slots`). A slot is an endowment package:
 * stocks, flows, Carbon base, Resilience, historical emissions share, terrain and climate exposure.
 * The climate timeline is an integer lookup table keyed by slot and Heat stage.
 */
import type { Forces, HeatStage, IndexValues, SlotId } from '../../shared/types.ts';

export type HitKey = 'treasury' | 'energy' | 'food' | 'prosperity' | 'wellbeing' | 'environment';
export type Hit = Partial<Record<HitKey, number>>;

export interface ClimateRoll {
  name: string;
  /** Hit when a d10 is at or below this. */
  upTo: number;
  hit: Hit;
  /** Flood or surge type: the Mangrove Belt makes it 1 smaller. */
  flood: boolean;
  /** Water hit: at Stage 4, Resilience above level 1 stops working for hard-limit slots. */
  water: boolean;
  tipping: 'methane' | 'dieback' | null;
  /** A drought roll (the Right to Stay Compact makes drought Food hits smaller). */
  drought: boolean;
}

export interface StageRow {
  /** Chronic per-turn losses (not reduced by Resilience). */
  drain: Hit;
  /** Heat drain, cancelled by Heat-Safe Work. */
  heat: Hit;
  /** Climate gains per turn. */
  gain: Hit;
  rolls: ClimateRoll[];
  /** Extra Treasury upkeep (permafrost damage). */
  upkeep: number;
  /** Delta: the Food drain part that a Water Treaty with an upstream nation or Resilience 2+ removes. */
  waterWaivable: number;
  /** Boreal: +Treasury per turn from the Arctic sea lane if Navy is 1 or more. */
  lane: number;
  /** Hard limit on water hits: Resilience counts at most 1 (Atoll and Highland at Stage 4; Delta from Stage 3). */
  hardLimit: boolean;
  /** Atoll at Stage 4: loses a zone unless a Relocation Pact exists. */
  zoneLoss: boolean;
  /** Harbor at Stage 4: +1 to all flood damage. */
  floodBonus: number;
}

export interface ShockCard {
  title: string;
  text: string;
  hit: Hit;
}

export interface SlotData {
  id: SlotId;
  name: string;
  fullName: string;
  terrain: string;
  topHazard: string;
  start: { treasury: number; energy: number; food: number; forces: Partial<Forces>; index: IndexValues };
  flows: { treasury: number; energy: number; food: number; energyUpkeep: number };
  carbon: number;
  resilience: number;
  /** Historical emissions share in percent. */
  share: number;
  mostVulnerable: boolean;
  terrainDefense: number;
  island: boolean;
  upstream: boolean;
  downstream: boolean;
  /** Green Transition costs 2 here (solar or hydro). */
  cheapGreen: boolean;
  armyDiscount: number;
  /** +1 Treasury per active trade treaty, up to this many (Harbor's Trade Hub). */
  tradeHub: number;
  /** Canopy: Forest Payments while Carbon is 1 or less, paid from the fund. */
  forestPayments: boolean;
  /** Sunbelt: +1 Treasury per Carbon point cut below Turn 0 Carbon. */
  solarExport: boolean;
  landscape: string;
  landscapeStressed: string;
  zoneNames: [string, string, string];
  crisis: {
    droughtFood: number;
    /** 'stage': Heat stage; 'stage2': stage x 2; a number: fixed. Minus Resilience. */
    flood: 'stage' | 'stage2' | number;
    pandemicSeverity: number;
    pirates: boolean;
    refugeeOrigin: 'always' | 'stage3' | null;
  };
  climate: Record<HeatStage, StageRow>;
  shocks: Record<HeatStage, ShockCard>;
  briefing: string;
  /** Boreal: the Arctic coast (Arctic Opening rule and the Arctic Treaty). */
  arctic?: boolean;
}

const row = (r: Partial<StageRow>): StageRow => ({
  drain: {}, heat: {}, gain: {}, rolls: [], upkeep: 0, waterWaivable: 0, lane: 0, hardLimit: false, zoneLoss: false, floodBonus: 0,
  ...r,
});

const roll = (name: string, upTo: number, hit: Hit, opts: Partial<Pick<ClimateRoll, 'flood' | 'water' | 'tipping' | 'drought'>> = {}): ClimateRoll => ({
  name, upTo, hit, flood: opts.flood ?? false, water: opts.water ?? false, tipping: opts.tipping ?? null, drought: opts.drought ?? false,
});

export const SLOTS: Record<SlotId, SlotData> = {
  harbor: {
    id: 'harbor',
    name: 'Harbor',
    fullName: 'Coastal industrial megastate',
    terrain: 'Huge cities by the sea, with deep ports, big factories and banks. The land is low and flat.',
    topHazard: 'Storm surge on coastal cities',
    start: { treasury: 8, energy: -2, food: -2, forces: {}, index: { prosperity: 10, security: 0, wellbeing: 0, environment: -10 } },
    flows: { treasury: 0, energy: 0, food: 0, energyUpkeep: 0 },
    carbon: 4, resilience: 2, share: 19, mostVulnerable: false, terrainDefense: 0, island: false,
    upstream: false, downstream: false, cheapGreen: false, armyDiscount: 0, tradeHub: 2, forestPayments: false, solarExport: false,
    landscape: 'geo-base-07-coastal-industrial', landscapeStressed: 'geo-stress-07-coastal-storm-surge',
    zoneNames: ['Portmere', 'Foundry Coast', 'Bay Towns'],
    crisis: { droughtFood: 0, flood: 'stage2', pandemicSeverity: 1, pirates: true, refugeeOrigin: null },
    climate: {
      1: row({}),
      2: row({ heat: { wellbeing: -1 } }),
      3: row({ heat: { wellbeing: -1 }, rolls: [roll('Storm surge', 2, { treasury: -3 }, { flood: true })] }),
      4: row({ heat: { wellbeing: -1 }, rolls: [roll('Storm surge', 3, { treasury: -3 }, { flood: true })], floodBonus: 1 }),
    },
    shocks: {
      1: { title: 'Hot summer nights', text: 'City heat records are set, and doctors ask people to check on older neighbors.', hit: {} },
      2: { title: 'King tides in the harbor', text: 'High tides flood the lowest docks. Port crews work through the night to move cargo.', hit: { treasury: -2 } },
      3: { title: 'Surge breaks the sea wall', text: 'A storm surge tops an old sea wall. Rescue teams move families to shelters.', hit: { treasury: -3, wellbeing: -2 } },
      4: { title: 'The ice sheets let go', text: 'Faster sea-level rise makes every flood worse along the coast.', hit: { treasury: -4 } },
    },
    briefing:
      'Your coast holds the world’s busiest ports and biggest factories. You are rich, but your cities sit on a low plain. Storm surges and summer heat grow as the world warms. Trade treaties pay you extra (Trade Hub).',
  },

  heartland: {
    id: 'heartland',
    name: 'Heartland',
    fullName: 'Temperate continental breadbasket',
    terrain: 'Wide inland plains, big rivers that flow to other nations, farm belts and forests that can burn.',
    topHazard: 'Heat waves and crop decline',
    start: { treasury: 5, energy: 2, food: 4, forces: { army: 1 }, index: { prosperity: 5, security: 5, wellbeing: -5, environment: -5 } },
    flows: { treasury: 2, energy: 0, food: 1, energyUpkeep: 0 },
    carbon: 4, resilience: 2, share: 19, mostVulnerable: false, terrainDefense: 0, island: false,
    upstream: true, downstream: false, cheapGreen: false, armyDiscount: 0, tradeHub: 0, forestPayments: false, solarExport: false,
    landscape: 'geo-base-02-breadbasket', landscapeStressed: 'geo-stress-04-breadbasket-drought',
    zoneNames: ['Prairie Crossing', 'Grain Belt', 'Timber Hills'],
    crisis: { droughtFood: 2, flood: 1, pandemicSeverity: 0, pirates: false, refugeeOrigin: null },
    climate: {
      1: row({}),
      2: row({ drain: { food: -1 } }),
      3: row({ drain: { food: -2 }, rolls: [roll('Wildfire', 2, { environment: -3, treasury: -2 })] }),
      4: row({ drain: { food: -3 }, rolls: [roll('Wildfire', 3, { environment: -3, treasury: -2 })] }),
    },
    shocks: {
      1: { title: 'A long dry summer', text: 'Farmers watch the sky. Extension agents share water-saving tips.', hit: {} },
      2: { title: 'Crops wilt in the heat', text: 'A heat wave cuts the wheat harvest. Farm families help each other bring in what is left.', hit: { food: -2 } },
      3: { title: 'Fire season starts early', text: 'Firefighters from every county hold the line against forest fires.', hit: { food: -2, treasury: -2 } },
      4: { title: 'The rain belt moves', text: 'The best farm weather moves north. Old farms struggle every year.', hit: { food: -3 } },
    },
    briefing:
      'Your plains feed much of the world, and your rivers flow to downstream nations. You start rich and ready (Resilience 2). As heat rises, harvests shrink from Stage 2 on. You can offer a Water Treaty to a Delta nation.',
  },

  sunbelt: {
    id: 'sunbelt',
    name: 'Sunbelt',
    fullName: 'Arid interior with oil fields',
    terrain: 'Desert and steppe with oil and gas fields, a few oases and irrigated valleys, and strong sunshine.',
    topHazard: 'Drought and extreme heat',
    start: { treasury: 11, energy: 6, food: -4, forces: {}, index: { prosperity: 5, security: 0, wellbeing: 0, environment: -5 } },
    flows: { treasury: 2, energy: 2, food: -1, energyUpkeep: 0 },
    carbon: 4, resilience: 1, share: 19, mostVulnerable: false, terrainDefense: 0, island: false,
    upstream: false, downstream: false, cheapGreen: true, armyDiscount: 0, tradeHub: 0, forestPayments: false, solarExport: true,
    landscape: 'geo-base-04-arid-interior', landscapeStressed: 'geo-stress-03-arid-drought',
    zoneNames: ['Oasis City', 'Salt Flats', 'Sun Valleys'],
    crisis: { droughtFood: 3, flood: 0, pandemicSeverity: 0, pirates: false, refugeeOrigin: 'stage3' },
    climate: {
      1: row({}),
      2: row({ heat: { prosperity: -1 } }),
      3: row({ heat: { prosperity: -2 }, rolls: [roll('Drought', 2, { food: -2 }, { water: true, drought: true })] }),
      4: row({ heat: { prosperity: -2 }, drain: { food: -2 }, rolls: [roll('Drought', 3, { food: -2 }, { water: true, drought: true })] }),
    },
    shocks: {
      1: { title: 'Record sunshine', text: 'Engineers note that the same sun that dries the land could power the cities.', hit: {} },
      2: { title: 'Heat stops the work day', text: 'Outdoor work stops in the afternoon heat. Clinics hand out water.', hit: { prosperity: -2 } },
      3: { title: 'The oases shrink', text: 'Wells run low. Water trucks and neighbors share what they have.', hit: { food: -2 } },
      4: { title: 'The valleys dry out', text: 'Irrigated valleys can no longer grow as much food.', hit: { food: -3 } },
    },
    briefing:
      'You are the top Energy producer, with oil fields and strong sunshine. You grow little food, and drought hits you hardest. Green Transition is cheap here (solar), and every Carbon point you cut earns +1 Treasury a turn from clean-power exports.',
  },

  boreal: {
    id: 'boreal',
    name: 'Boreal',
    fullName: 'Northern sub-Arctic dominion',
    terrain: 'Boreal forest, frozen ground (permafrost), a northern coast that opens as ice melts, oil and gas, and rivers that flow to others.',
    topHazard: 'Permafrost thaw damaging roads and towns',
    start: { treasury: 4, energy: 6, food: -3, forces: {}, index: { prosperity: 0, security: 0, wellbeing: 5, environment: -5 } },
    flows: { treasury: 0, energy: 0, food: 0, energyUpkeep: 1 },
    carbon: 4, resilience: 2, share: 19, mostVulnerable: false, terrainDefense: 0, island: false,
    upstream: true, downstream: false, cheapGreen: false, armyDiscount: 0, tradeHub: 0, forestPayments: false, solarExport: false,
    landscape: 'geo-base-05-arctic', landscapeStressed: 'geo-stress-05-arctic-thaw',
    arctic: true,
    zoneNames: ['Northgate', 'Taiga March', 'Ice Coast'],
    crisis: { droughtFood: 0, flood: 1, pandemicSeverity: 0, pirates: false, refugeeOrigin: null },
    climate: {
      1: row({}),
      2: row({ gain: { food: 1 }, lane: 1 }),
      3: row({ gain: { food: 1 }, lane: 1, upkeep: 1, rolls: [roll('Methane release', 3, {}, { tipping: 'methane' })] }),
      4: row({ lane: 1, upkeep: 2, rolls: [roll('Methane release', 3, {}, { tipping: 'methane' })] }),
    },
    shocks: {
      1: { title: 'A warmer winter', text: 'The ice road opens late this year. Trucks wait at the edge of the lake.', hit: {} },
      2: { title: 'The sea lane opens', text: 'Summer ice melts and ships can pass the north coast. Farms move north too.', hit: { treasury: 1 } },
      3: { title: 'The ground sinks', text: 'Thawing ground cracks roads and pipes. Road crews work to keep towns connected.', hit: { treasury: -2 } },
      4: { title: 'The gains run out', text: 'The new farmland dries out and repairs keep rising.', hit: { treasury: -3 } },
    },
    briefing:
      'You are cold, rich in energy and home to rivers that flow to other nations. At first, warming helps you: new farmland and a new sea lane. Then thawing ground damages roads and towns and can release methane that heats the whole world.',
  },

  atoll: {
    id: 'atoll',
    name: 'Atoll',
    fullName: 'Low-lying island archipelago',
    terrain: 'Three low-lying island groups with a huge ocean zone and coral reefs. No land border; the Navy defends it.',
    topHazard: 'Sea-level rise and cyclones',
    start: { treasury: 0, energy: -4, food: -2, forces: { navy: 2 }, index: { prosperity: 0, security: -5, wellbeing: -5, environment: 10 } },
    flows: { treasury: 2, energy: 0, food: 1, energyUpkeep: 0 },
    carbon: 0, resilience: 0, share: 4, mostVulnerable: true, terrainDefense: 10, island: true,
    upstream: false, downstream: false, cheapGreen: false, armyDiscount: 0, tradeHub: 0, forestPayments: false, solarExport: false,
    landscape: 'geo-base-01-island', landscapeStressed: 'geo-stress-01-island-sea-rise',
    zoneNames: ['Lagoon Capital', 'Windward Isles', 'Leeward Isles'],
    crisis: { droughtFood: 1, flood: 'stage', pandemicSeverity: 0, pirates: true, refugeeOrigin: 'always' },
    climate: {
      1: row({ rolls: [roll('Cyclone', 1, { treasury: -2, wellbeing: -3 }, { flood: true })] }),
      2: row({ drain: { treasury: -1 }, rolls: [roll('Cyclone', 2, { treasury: -2, wellbeing: -3 }, { flood: true })] }),
      3: row({ drain: { treasury: -1, food: -1 }, rolls: [roll('Cyclone', 3, { treasury: -2, wellbeing: -3 }, { flood: true, water: true })] }),
      4: row({ drain: { treasury: -1, food: -1 }, rolls: [roll('Cyclone', 3, { treasury: -2, wellbeing: -3 }, { flood: true, water: true })], hardLimit: true, zoneLoss: true }),
    },
    shocks: {
      1: { title: 'High water at the full moon', text: 'Families raise their homes on blocks. Neighbors help move boats to higher ground.', hit: {} },
      2: { title: 'The reef turns white', text: 'Warm water bleaches the coral. Fewer visitors come to dive.', hit: { treasury: -2 } },
      3: { title: 'Salt in the wells', text: 'Sea water seeps into fresh water. Island councils ship in drinking water.', hit: { food: -2 } },
      4: { title: 'The sea claims an island', text: 'One island group can no longer be lived on. Families need a new home.', hit: { wellbeing: -3 } },
    },
    briefing:
      'Your islands are rich in fish and shipping, and your Navy is strong. You add almost nothing to warming, but you face its worst risks: cyclones, reef loss and rising seas. Climate hits here are capped at 4 Treasury and 3 Well-being each, and you hold an extra vote on climate items at the World Assembly.',
  },

  delta: {
    id: 'delta',
    name: 'Delta',
    fullName: 'River delta federation',
    terrain: 'A crowded, rich river delta with heavy rains. It sits where the rivers from the Highland, Heartland and Boreal reach the sea.',
    topHazard: 'Monsoon and river floods',
    start: { treasury: 1, energy: -2, food: 6, forces: { army: 1 }, index: { prosperity: -5, security: 5, wellbeing: 0, environment: 0 } },
    flows: { treasury: 1, energy: 0, food: 2, energyUpkeep: 0 },
    carbon: 1, resilience: 1, share: 8, mostVulnerable: true, terrainDefense: 0, island: false,
    upstream: false, downstream: true, cheapGreen: false, armyDiscount: 1, tradeHub: 0, forestPayments: false, solarExport: false,
    landscape: 'geo-base-03-delta', landscapeStressed: 'geo-stress-02-delta-storm-flood',
    zoneNames: ['Delta City', 'Rice Plains', 'Mangrove Coast'],
    crisis: { droughtFood: 1, flood: 'stage', pandemicSeverity: 1, pirates: false, refugeeOrigin: 'always' },
    climate: {
      1: row({ rolls: [roll('Monsoon flood', 1, { food: -2 }, { flood: true, water: true })] }),
      2: row({ waterWaivable: 1, heat: { prosperity: -1 }, rolls: [roll('Monsoon flood', 1, { food: -2 }, { flood: true, water: true })] }),
      3: row({
        waterWaivable: 1, drain: { food: -1 }, heat: { prosperity: -1 },
        rolls: [roll('River flood', 4, { treasury: -3, wellbeing: -3, food: -2 }, { flood: true, water: true })],
        hardLimit: true,
      }),
      4: row({
        waterWaivable: 1, drain: { food: -1 }, heat: { prosperity: -1 },
        rolls: [roll('River flood', 6, { treasury: -3, wellbeing: -3, food: -2 }, { flood: true, water: true })],
        hardLimit: true,
      }),
    },
    shocks: {
      1: { title: 'Heavy rains come early', text: 'Village councils clear the drains and check the river banks.', hit: {} },
      2: { title: 'Heat in the rice fields', text: 'Farm workers start at dawn to avoid the worst heat.', hit: { food: -2 } },
      3: { title: 'Salt water in the fields', text: 'The sea pushes salt into rice paddies. Farmers test salt-tolerant seeds.', hit: { food: -2, wellbeing: -2 } },
      4: { title: 'The great flood', text: 'The river rises higher than anyone remembers. Rescue boats reach every village.', hit: { treasury: -3, wellbeing: -3 } },
    },
    briefing:
      'Your delta grows the world’s biggest food surplus and has the largest army. Your water comes from upstream nations. From Stage 2, a Water Treaty with an upstream nation (Highland, Heartland or Boreal) or Resilience 2 protects your harvest. From Stage 3, big river floods come often (about 4 turns in 10), and against water hits your Resilience counts at most 1. A Mangrove Belt and a Mutual Aid Compact still help in full.',
  },

  highland: {
    id: 'highland',
    name: 'Highland',
    fullName: 'Mountain and glacier republic',
    terrain: 'High mountains, glaciers and deep valleys with hydropower dams. Its rivers feed the Delta.',
    topHazard: 'Glacier loss and outburst floods',
    start: { treasury: -6, energy: 2, food: -1, forces: {}, index: { prosperity: -5, security: 0, wellbeing: 0, environment: 5 } },
    flows: { treasury: -2, energy: 1, food: 0, energyUpkeep: 0 },
    carbon: 1, resilience: 1, share: 4, mostVulnerable: true, terrainDefense: 10, island: false,
    upstream: true, downstream: false, cheapGreen: true, armyDiscount: 0, tradeHub: 0, forestPayments: false, solarExport: false,
    landscape: 'geo-base-06-mountain', landscapeStressed: 'geo-stress-06-mountain-glacier-loss',
    zoneNames: ['Summit City', 'Glacier Valleys', 'Dam Country'],
    crisis: { droughtFood: 1, flood: 0, pandemicSeverity: 0, pirates: false, refugeeOrigin: null },
    climate: {
      1: row({ gain: { energy: 1 } }),
      2: row({ gain: { energy: 1 } }),
      3: row({ drain: { energy: -1, food: -1 }, rolls: [roll('Glacier-lake flood', 2, { treasury: -3, wellbeing: -3 }, { flood: true, water: true })] }),
      4: row({ drain: { energy: -1, food: -1 }, rolls: [roll('Glacier-lake flood', 3, { treasury: -3, wellbeing: -3 }, { flood: true, water: true })], hardLimit: true }),
    },
    shocks: {
      1: { title: 'Rivers run high', text: 'Melting ice fills the dams. The power plants hum all summer.', hit: {} },
      2: { title: 'Rockslides close a pass', text: 'Road crews and mountain guides reopen the valley road.', hit: { treasury: -1 } },
      3: { title: 'Peak water has passed', text: 'The glaciers are smaller now, and rivers run lower each year.', hit: { energy: -2, food: -1 } },
      4: { title: 'The last glaciers', text: 'Most glaciers are gone. Dams and farms get less water.', hit: { energy: -2, food: -2 } },
    },
    briefing:
      'Your mountains hold glaciers and dams, and your rivers feed the Delta. Hydropower makes Green Transition cheap, and mountains help you defend. Melting ice helps at first, then rivers shrink and glacier lakes can burst.',
  },

  canopy: {
    id: 'canopy',
    name: 'Canopy',
    fullName: 'Tropical rainforest commonwealth',
    terrain: 'Tropical rainforest, river basins and a farm frontier, with rich wildlife and a forest that stores carbon.',
    topHazard: 'Heat, fire and forest dieback',
    start: { treasury: -1, energy: 0, food: 2, forces: {}, index: { prosperity: -5, security: 0, wellbeing: -5, environment: 10 } },
    flows: { treasury: 0, energy: 0, food: 0, energyUpkeep: 0 },
    carbon: 2, resilience: 0, share: 8, mostVulnerable: true, terrainDefense: 0, island: false,
    upstream: false, downstream: false, cheapGreen: false, armyDiscount: 0, tradeHub: 0, forestPayments: true, solarExport: false,
    landscape: 'geo-base-08-forested-highland', landscapeStressed: 'geo-stress-08-forest-wildfire',
    zoneNames: ['Canopy City', 'River Basin', 'Forest Frontier'],
    crisis: { droughtFood: 1, flood: 1, pandemicSeverity: 1, pirates: false, refugeeOrigin: null },
    climate: {
      1: row({ rolls: [roll('Forest fire', 1, { environment: -3 })] }),
      2: row({ heat: { prosperity: -1 }, rolls: [roll('Forest fire', 1, { environment: -3 })] }),
      3: row({ heat: { prosperity: -1 }, rolls: [roll('Forest fire', 2, { environment: -3 }), roll('Forest dieback', 2, {}, { tipping: 'dieback' })] }),
      4: row({ heat: { prosperity: -1 }, rolls: [roll('Forest fire', 2, { environment: -3 }), roll('Forest dieback', 4, {}, { tipping: 'dieback' })] }),
    },
    shocks: {
      1: { title: 'A smoky dry season', text: 'Rangers and villagers watch for fires from the river towers.', hit: {} },
      2: { title: 'Heat on the farm frontier', text: 'Farm work slows in the heat. Clinics set up cooling centers.', hit: { prosperity: -2 } },
      3: { title: 'The forest dries', text: 'Parts of the rainforest are drying out. Scientists and rangers try to protect them.', hit: { environment: -3 } },
      4: { title: 'The forest fades', text: 'Large areas of forest are turning to dry grassland.', hit: { environment: -5, food: -1 } },
    },
    briefing:
      'Your rainforest stores huge amounts of carbon and is home to rich wildlife. You can clear forest for farms (more Food and Treasury, but more Carbon). While your Carbon is 1 or less, the Loss and Damage Fund pays you Forest Payments. At high heat the forest itself is at risk.',
  },
};
